import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Video as VideoIcon,
  Upload,
  Loader2,
  Star,
  Eye,
  EyeOff,
  Play,
  Pencil,
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatSize = (bytes) => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
};

const AdminVideosPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("adminToken");
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    is_presentation: false,
    is_visible: true,
    file: null,
  });

  const fetchVideos = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/gallery-videos?token=${token}`);
      setVideos(res.data);
    } catch (e) {
      toast.error("Erreur lors du chargement des vidéos");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      navigate("/espace-pro-fastandcar");
      return;
    }
    fetchVideos();
  }, [token, navigate, fetchVideos]);

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      is_presentation: false,
      is_visible: true,
      file: null,
    });
    setEditingVideo(null);
    setUploadProgress(0);
  };

  const openAddDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const openEditDialog = (video) => {
    setEditingVideo(video);
    setFormData({
      title: video.title,
      description: video.description || "",
      is_presentation: video.is_presentation,
      is_visible: video.is_visible,
      file: null,
    });
    setIsDialogOpen(true);
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    const allowedTypes = ["video/mp4", "video/quicktime", "video/webm", "video/x-m4v", "video/mpeg"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Format non supporté. Utilisez MP4, MOV ou WebM.");
      return;
    }
    setFormData({ ...formData, file, title: formData.title || file.name.replace(/\.[^.]+$/, "") });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    handleFileSelect(file);
  };

  const handleSave = async () => {
    if (editingVideo) {
      // Update metadata only
      try {
        await axios.put(`${API}/gallery-videos/${editingVideo.id}?token=${token}`, {
          title: formData.title,
          description: formData.description,
          is_presentation: formData.is_presentation,
          is_visible: formData.is_visible,
        });
        toast.success("Vidéo mise à jour");
        setIsDialogOpen(false);
        resetForm();
        fetchVideos();
      } catch (e) {
        toast.error(e.response?.data?.detail || "Erreur de mise à jour");
      }
      return;
    }

    // New upload
    if (!formData.file) {
      toast.error("Sélectionnez une vidéo à uploader");
      return;
    }
    if (!formData.title.trim()) {
      toast.error("Le titre est obligatoire");
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);
      const data = new FormData();
      data.append("file", formData.file);
      const params = new URLSearchParams({
        title: formData.title.trim(),
        is_presentation: formData.is_presentation,
        token: token,
      });
      if (formData.description) params.append("description", formData.description);

      await axios.post(`${API}/upload-video?${params.toString()}`, data, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) {
            setUploadProgress(Math.round((e.loaded * 100) / e.total));
          }
        },
        timeout: 300000, // 5 min for large videos
      });

      toast.success("Vidéo uploadée avec succès !");
      setIsDialogOpen(false);
      resetForm();
      fetchVideos();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'upload");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm("Supprimer définitivement cette vidéo ?")) return;
    try {
      await axios.delete(`${API}/gallery-videos/${videoId}?token=${token}`);
      toast.success("Vidéo supprimée");
      fetchVideos();
    } catch (e) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const toggleVisibility = async (video) => {
    try {
      await axios.put(`${API}/gallery-videos/${video.id}?token=${token}`, {
        is_visible: !video.is_visible,
      });
      fetchVideos();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/espace-pro-fastandcar/dashboard")}
              data-testid="back-to-dashboard"
              className="text-white hover:bg-zinc-900"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <div>
              <h1 className="font-unbounded text-3xl font-black">Gestion des vidéos</h1>
              <p className="text-zinc-400 text-sm mt-1">
                Uploadez vos vidéos de tour de véhicules et votre vidéo de présentation
              </p>
            </div>
          </div>
          <Button
            onClick={openAddDialog}
            data-testid="add-video-btn"
            className="bg-neon-lime text-black font-bold hover:bg-neon-lime/90"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle vidéo
          </Button>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-neon-lime" />
          </div>
        ) : videos.length === 0 ? (
          <div className="border border-zinc-800 p-16 text-center">
            <VideoIcon className="w-16 h-16 mx-auto text-zinc-700 mb-4" />
            <p className="text-zinc-400 mb-4">Aucune vidéo uploadée pour le moment</p>
            <Button
              onClick={openAddDialog}
              className="bg-neon-lime text-black font-bold"
              data-testid="first-video-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Uploader ma première vidéo
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.map((video) => (
              <div
                key={video.id}
                data-testid={`video-card-${video.id}`}
                className="border border-zinc-800 bg-zinc-900/50 overflow-hidden group hover:border-neon-lime/50 transition-colors"
              >
                <div className="relative aspect-video bg-black">
                  <video
                    src={`${API}/videos/${video.id}`}
                    className="w-full h-full object-cover"
                    preload="metadata"
                    muted
                    playsInline
                  />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Play className="w-12 h-12 text-white" />
                  </div>
                  <div className="absolute top-2 left-2 flex gap-2">
                    {video.is_presentation && (
                      <span className="bg-neon-lime text-black text-xs font-bold px-2 py-1 flex items-center gap-1">
                        <Star className="w-3 h-3" /> Présentation
                      </span>
                    )}
                    {!video.is_visible && (
                      <span className="bg-zinc-700 text-white text-xs font-bold px-2 py-1">
                        Masquée
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-white truncate" title={video.title}>
                    {video.title}
                  </h3>
                  {video.description && (
                    <p className="text-zinc-400 text-sm mt-1 line-clamp-2">
                      {video.description}
                    </p>
                  )}
                  <div className="text-xs text-zinc-500 mt-2">
                    {formatSize(video.size)} • {new Date(video.created_at).toLocaleDateString("fr-FR")}
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => toggleVisibility(video)}
                      data-testid={`toggle-visibility-${video.id}`}
                      className="border-zinc-700 text-white hover:bg-zinc-800 flex-1"
                    >
                      {video.is_visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditDialog(video)}
                      data-testid={`edit-video-${video.id}`}
                      className="border-zinc-700 text-white hover:bg-zinc-800 flex-1"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(video.id)}
                      data-testid={`delete-video-${video.id}`}
                      className="border-red-900 text-red-400 hover:bg-red-950"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          if (!uploading) {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }
        }}>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-unbounded">
                {editingVideo ? "Modifier la vidéo" : "Nouvelle vidéo"}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              {!editingVideo && (
                <div
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="video-dropzone"
                  className={`border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
                    isDragging ? "border-neon-lime bg-neon-lime/5" : "border-zinc-700 hover:border-neon-lime/50"
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="video/mp4,video/quicktime,video/webm,video/x-m4v"
                    className="hidden"
                    onChange={(e) => handleFileSelect(e.target.files?.[0])}
                  />
                  {formData.file ? (
                    <div>
                      <VideoIcon className="w-12 h-12 mx-auto text-neon-lime mb-3" />
                      <p className="text-white font-bold truncate">{formData.file.name}</p>
                      <p className="text-zinc-400 text-sm mt-1">{formatSize(formData.file.size)}</p>
                      <p className="text-zinc-500 text-xs mt-2">Cliquez pour changer</p>
                    </div>
                  ) : (
                    <div>
                      <Upload className="w-12 h-12 mx-auto text-zinc-500 mb-3" />
                      <p className="text-white font-bold">Glissez votre vidéo ici</p>
                      <p className="text-zinc-400 text-sm mt-1">
                        MP4, MOV ou WebM — aucune limite de taille
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div>
                <Label className="text-white">Titre *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Ex: Audi RS6 — Tour complet intérieur et extérieur"
                  data-testid="video-title"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>

              <div>
                <Label className="text-white">Description (optionnelle)</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Détails sur la vidéo…"
                  rows={3}
                  data-testid="video-description"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>

              <div className="space-y-3 border-t border-zinc-800 pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-white flex items-center gap-2">
                      <Star className="w-4 h-4 text-neon-lime" />
                      Vidéo de présentation du site
                    </Label>
                    <p className="text-zinc-400 text-xs mt-1">
                      Affichée sur la page d'accueil. Une seule vidéo de présentation à la fois.
                    </p>
                  </div>
                  <Switch
                    checked={formData.is_presentation}
                    onCheckedChange={(c) => setFormData({ ...formData, is_presentation: c })}
                    data-testid="video-presentation-toggle"
                  />
                </div>
                {editingVideo && (
                  <div className="flex items-center justify-between">
                    <Label className="text-white">Visible sur le site</Label>
                    <Switch
                      checked={formData.is_visible}
                      onCheckedChange={(c) => setFormData({ ...formData, is_visible: c })}
                    />
                  </div>
                )}
              </div>

              {uploading && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-white">Upload en cours…</span>
                    <span className="text-neon-lime font-bold">{uploadProgress}%</span>
                  </div>
                  <div className="h-2 bg-zinc-800 overflow-hidden">
                    <div
                      className="h-full bg-neon-lime transition-all duration-200"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  disabled={uploading}
                  className="border-zinc-700 text-white hover:bg-zinc-800 flex-1"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={uploading}
                  data-testid="save-video-btn"
                  className="bg-neon-lime text-black font-bold hover:bg-neon-lime/90 flex-1"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Upload…
                    </>
                  ) : editingVideo ? (
                    "Enregistrer"
                  ) : (
                    "Uploader"
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminVideosPage;
